import { create } from 'zustand';
import { kkmovieService } from '@/services/kkmovieService';
import {
  KKMovie,
  CreateKKMovieDto,
  UpdateKKMovieDto,
  GetPaginationKKMoviesDto,
  KKMovieStatusEnum,
  KKMovieTypeEnum,
} from '@/types/kkmovie';

interface KKMovieState {
  kkmovieList: KKMovie[];
  selectedMovie: KKMovie | null;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  filters: {
    search: string;
    status: KKMovieStatusEnum | '';
    type: KKMovieTypeEnum | '';
    source: string;
  };
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;

  // Actions
  fetchKKMovies: (query?: GetPaginationKKMoviesDto) => Promise<void>;
  fetchKKMovieById: (id: string) => Promise<KKMovie | null>;
  fetchKKMovieByUrl: (url: string) => Promise<KKMovie | null>;
  createKKMovie: (data: CreateKKMovieDto) => Promise<boolean>;
  updateKKMovie: (id: string, data: UpdateKKMovieDto) => Promise<boolean>;
  deleteKKMovie: (id: string) => Promise<boolean>;
  setFilters: (filters: Partial<KKMovieState['filters']>) => void;
  setPage: (page: number) => void;
  setSelectedMovie: (movie: KKMovie | null) => void;
}

export const useKKMovieStore = create<KKMovieState>()((set, get) => ({
  kkmovieList: [],
  selectedMovie: null,
  pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
  filters: {
    search: '',
    status: '',
    type: '',
    source: '',
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,

  fetchKKMovies: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const { pagination, filters } = get();
      const params: GetPaginationKKMoviesDto = query || {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.type && { type: filters.type }),
        ...(filters.source && { source: filters.source }),
      };

      const res = await kkmovieService.getPaginationKKMovies(params);
      if (res.ok && res.data) {
        const data: any = res.data.data;
        set({
          kkmovieList: data?.data || [],
          pagination: data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
          isLoading: false,
        });
      } else {
        set({ isLoading: false, error: (res as any)?.data?.message || 'Lỗi khi tải KKMovies' });
      }
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Lỗi khi tải KKMovies' });
    }
  },

  fetchKKMovieById: async (id) => {
    try {
      const res = await kkmovieService.getKKMovieById(id);
      if (res.ok && res.data) {
        const responseData = res.data as any;
        const movie = responseData?.data?.data || responseData?.data || responseData;
        set({ selectedMovie: movie as KKMovie });
        return movie as KKMovie;
      }
      return null;
    } catch {
      return null;
    }
  },

  fetchKKMovieByUrl: async (url) => {
    try {
      const res = await kkmovieService.getKKMovieByUrl(url);
      if (res.ok && res.data) {
        const responseData = res.data as any;
        const movie = responseData?.data?.data || responseData?.data || responseData;
        set({ selectedMovie: movie as KKMovie });
        return movie as KKMovie;
      }
      return null;
    } catch {
      return null;
    }
  },

  createKKMovie: async (data) => {
    set({ isCreating: true, error: null });
    try {
      const res = await kkmovieService.create(data);
      set({ isCreating: false });
      if (res.ok) {
        await get().fetchKKMovies();
        return true;
      }
      return false;
    } catch (e) {
      set({ isCreating: false, error: e instanceof Error ? e.message : 'Lỗi khi tạo KKMovie' });
      return false;
    }
  },

  updateKKMovie: async (id, data) => {
    set({ isUpdating: true, error: null });
    try {
      const res = await kkmovieService.updateKKMovie(id, data);
      set({ isUpdating: false });
      if (res.ok) {
        await get().fetchKKMovies();
        return true;
      }
      return false;
    } catch (e) {
      set({ isUpdating: false, error: e instanceof Error ? e.message : 'Lỗi khi cập nhật KKMovie' });
      return false;
    }
  },

  deleteKKMovie: async (id) => {
    set({ isDeleting: true, error: null });
    try {
      const res = await kkmovieService.deleteKKMovie(id);
      set({ isDeleting: false });
      if (res.ok) {
        await get().fetchKKMovies();
        return true;
      }
      return false;
    } catch (e) {
      set({ isDeleting: false, error: e instanceof Error ? e.message : 'Lỗi khi xóa KKMovie' });
      return false;
    }
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 },
    }));
  },

  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }));
  },

  setSelectedMovie: (movie) => {
    set({ selectedMovie: movie });
  },
}));
